import { GET_TIMETABLE_QUERY, getTimetable, useGetTimetablesQuery } from '@schooly/api';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  buildGroupConflictMap,
  GroupConflict,
  selectOtherPublishedTimetables,
} from './groupConflicts';

type UsePublishedGroupConflictsParams = {
  schoolId: string;
  schoolYearId: string;
  currentStructureId: string;
};

type UsePublishedGroupConflictsResult = {
  conflictMap: Map<string, GroupConflict>;
  isLoading: boolean;
};

export const usePublishedGroupConflicts = ({
  schoolId,
  schoolYearId,
  currentStructureId,
}: UsePublishedGroupConflictsParams): UsePublishedGroupConflictsResult => {
  const { data, isInitialLoading: isLoadingList } = useGetTimetablesQuery(
    { school_id: schoolId, school_year_id: schoolYearId },
    {
      enabled: !!schoolId && !!schoolYearId,
      refetchOnMount: 'always',
    },
  );

  const otherPublishedTimetables = useMemo(
    () =>
      data ? selectOtherPublishedTimetables(data.timetables, currentStructureId, schoolYearId) : [],
    [data, currentStructureId, schoolYearId],
  );

  const detailQueries = useQueries({
    queries: otherPublishedTimetables.map((timetable) => ({
      queryKey: [GET_TIMETABLE_QUERY, { timetableId: timetable.id }],
      queryFn: () => getTimetable({ timetableId: timetable.id }),
      enabled: !!timetable.id,
      refetchOnMount: 'always' as const,
    })),
  });

  const conflictMap = useMemo(
    () =>
      buildGroupConflictMap(
        otherPublishedTimetables.map((timetable, index) => ({
          id: timetable.id,
          name: timetable.name,
          groupIds: (detailQueries[index]?.data?.groups ?? []).map((group) => group.id),
        })),
      ),
    [otherPublishedTimetables, detailQueries],
  );

  const isLoading = isLoadingList || detailQueries.some((query) => query.isFetching);

  return { conflictMap, isLoading };
};
